import React, { useContext } from 'react';

import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';

import Typography from 'components/Typography';

import DescriptionContext from './DescriptionContext';
import DescriptionValue from './DescriptionValue';
import { IDescriptionItem } from './interfaces';
import { commaListParser, minMaxToStringArrayParser } from './parsers';

export const parsers: { [index: string]: any } = {
  commaList: commaListParser,
  minMaxToStringArray: minMaxToStringArrayParser,
};

export const DescriptionItem: React.FC<IDescriptionItem> = ({
  label,
  field = '',
  type: typeProp = 'text',
  valueParser,
  onClick,
  value: valueProp,
  placeholder = '—',
  ...otherProps
}) => {
  const { data, stripEmpty } = useContext(DescriptionContext);

  let value = typeProp === 'skeleton' ? true : valueProp;
  let type = typeProp;

  if (value === undefined) {
    value = get(data, field);

    if (!field) {
      value = data;
    }

    if (valueParser) {
      value = isString(valueParser)
        ? parsers[valueParser](value)
        : valueParser(value);
    }
  }

  const isEmpty = isNil(value) || value?.length === 0;

  if (isEmpty) {
    if (placeholder) {
      // render placeholder for empty value
      if (isString(placeholder)) {
        value = placeholder;
        type = 'text';
      } else {
        type = () => placeholder;
      }
    } else if (stripEmpty) {
      // omit empty item without placeholder
      return null;
    }
  }

  if (!field && isEmpty) {
    throw new Error(`DescriptionItem: "value" or "field" must be provided`);
  }

  const handleValueClick = (e: React.MouseEvent<HTMLElement>) => {
    onClick?.(e, value); // TODO: need handler?
  };

  return (
    <div data-label={label}>
      <div>
        <Typography variant={'overline'} color={'medium-shade'}>
          {label}
        </Typography>
      </div>
      <div>
        <DescriptionValue
          value={value}
          type={type}
          onClick={handleValueClick}
          {...otherProps}
        />
      </div>
    </div>
  );
};
